import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api'; // ✅ FIXED: Use centralized API service
import './AdminVoterCertificates.css';

const AdminVoterCertificates = () => {
  const { t } = useTranslation();
  const [certificates, setCertificates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedConstituency, setSelectedConstituency] = useState('');
  const [districts, setDistricts] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  
  // View modal
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ✅ FIXED: Use centralized API without axios
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCertificates();
  }, [currentPage, pageSize]);

  const loadData = async () => {
    try {
      await Promise.all([
        loadCertificates(),
        loadStats(),
        loadDistricts(),
        loadConstituencies()
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    }
  };

  const loadCertificates = async () => {
    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getAllVoterCertificates(currentPage, pageSize);

      if (response.success) {
        setCertificates(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error loading certificates:', err);
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getVoterCertificateStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading stats:', err);
      // Don't set error, just log - stats are optional
    }
  };

  const loadDistricts = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getCertificateDistricts();
      if (response.success) {
        setDistricts(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading districts:', err);
    }
  };

  const loadConstituencies = async () => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getCertificateConstituencies();
      if (response.success) {
        setConstituencies(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading constituencies:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadCertificates();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.searchVoterCertificates(searchQuery);

      if (response.success) {
        setCertificates(response.data);
        setTotalCount(response.count);
      }
    } catch (err) {
      console.error('❌ Error searching:', err);
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const filterByDistrict = async (district) => {
    if (!district) {
      loadCertificates();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getCertificatesByDistrict(district, currentPage, pageSize);

      if (response.success) {
        setCertificates(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error filtering by district:', err);
      setError('Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const filterByConstituency = async (constituency) => {
    if (!constituency) {
      loadCertificates();
      return;
    }

    try {
      setLoading(true);
      // ✅ FIXED: Use API service method
      const response = await api.getCertificatesByConstituency(constituency, currentPage, pageSize);

      if (response.success) {
        setCertificates(response.data);
        setTotalPages(response.totalPages);
        setTotalCount(response.totalCount);
      }
    } catch (err) {
      console.error('❌ Error filtering by constituency:', err);
      setError('Filter failed');
    } finally {
      setLoading(false);
    }
  };

  const viewCertificate = async (certificateId) => {
    try {
      // ✅ FIXED: Use API service method
      const response = await api.getVoterCertificateById(certificateId);

      if (response.success) {
        setSelectedCertificate(response.data);
        setShowModal(true);
      }
    } catch (err) {
      console.error('❌ Error viewing certificate:', err);
      alert('Failed to load certificate details');
    }
  };

  const exportToCSV = () => {
    const headers = ['Certificate ID', 'Voter ID', 'Full Name', 'Phone', 'Age', 'Gender', 'District', 'Constituency', 'Submitted At', 'Downloads'];
    
    const rows = certificates.map(cert => [
      cert.certificateId,
      cert.voterId,
      cert.fullName,
      cert.phoneNumber,
      cert.age,
      cert.gender,
      cert.district,
      cert.constituency,
      new Date(cert.submittedAt).toLocaleString(),
      cert.downloadCount
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `voter-certificates-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading && certificates.length === 0) {
    return (
      <div className="admin-voter-certs-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-voter-certs-page">
      {/* Header */}
      <div className="admin-cert-header">
        <h1>
          <i className="fas fa-id-card"></i>
          Voter Certificates Management
        </h1>
        <p>Manage and monitor all voter participation certificates</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="cert-stats-grid">
          <div className="cert-stat-card total">
            <div className="stat-icon">
              <i className="fas fa-certificate"></i>
            </div>
            <div className="stat-content">
              <h3>Total Certificates</h3>
              <p className="stat-value">{stats.totalCertificates}</p>
            </div>
          </div>

          <div className="cert-stat-card verified">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <h3>Verified</h3>
              <p className="stat-value">{stats.verifiedCertificates || 0}</p>
            </div>
          </div>

          <div className="cert-stat-card downloads">
            <div className="stat-icon">
              <i className="fas fa-download"></i>
            </div>
            <div className="stat-content">
              <h3>Total Downloads</h3>
              <p className="stat-value">{stats.totalDownloads || 0}</p>
            </div>
          </div>

          <div className="cert-stat-card gender">
            <div className="stat-icon">
              <i className="fas fa-venus-mars"></i>
            </div>
            <div className="stat-content">
              <h3>Gender Distribution</h3>
              <div className="gender-breakdown">
                {stats.byGender && stats.byGender.map((g, i) => (
                  <span key={i}>{g.gender}: {g.count}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="cert-filters-container">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search by name, phone, voter ID, certificate ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="search-btn">
            <i className="fas fa-search"></i>
            Search
          </button>
        </div>

        <div className="filter-section">
          <select
            value={selectedDistrict}
            onChange={(e) => {
              setSelectedDistrict(e.target.value);
              filterByDistrict(e.target.value);
            }}
          >
            <option value="">All Districts</option>
            {districts.map((d, i) => (
              <option key={i} value={d.district}>
                {d.district} ({d.certificateCount})
              </option>
            ))}
          </select>

          <select
            value={selectedConstituency}
            onChange={(e) => {
              setSelectedConstituency(e.target.value);
              filterByConstituency(e.target.value);
            }}
          >
            <option value="">All Constituencies</option>
            {constituencies.map((c, i) => (
              <option key={i} value={c.constituency}>
                {c.constituency} ({c.certificateCount})
              </option>
            ))}
          </select>

          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>

          <button onClick={exportToCSV} className="export-btn">
            <i className="fas fa-file-csv"></i>
            Export CSV
          </button>

          <button onClick={loadData} className="refresh-btn">
            <i className="fas fa-sync-alt"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {/* Certificates Table */}
      <div className="cert-table-container">
        <div className="table-header-info">
          <h3>All Certificates ({totalCount})</h3>
          <p>Page {currentPage} of {totalPages}</p>
        </div>

        <table className="cert-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Certificate ID</th>
              <th>Voter ID</th>
              <th>Full Name</th>
              <th>Phone</th>
              <th>Age</th>
              <th>Gender</th>
              <th>District</th>
              <th>Constituency</th>
              <th>Submitted</th>
              <th>Downloads</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {certificates.length === 0 ? (
              <tr>
                <td colSpan="13" className="no-data">
                  <i className="fas fa-inbox"></i>
                  <p>No certificates found</p>
                </td>
              </tr>
            ) : (
              certificates.map((cert, index) => (
                <tr key={cert.id || index}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td className="cert-id">{cert.certificateId}</td>
                  <td>{cert.voterId}</td>
                  <td className="name-cell">{cert.fullName}</td>
                  <td>{cert.phoneNumber}</td>
                  <td>{cert.age}</td>
                  <td>
                    <span className={`gender-badge ${cert.gender.toLowerCase()}`}>
                      {cert.gender}
                    </span>
                  </td>
                  <td>{cert.district}</td>
                  <td className="constituency-cell">{cert.constituency}</td>
                  <td>{new Date(cert.submittedAt).toLocaleDateString()}</td>
                  <td>
                    <span className="download-count">
                      <i className="fas fa-download"></i>
                      {cert.downloadCount}
                    </span>
                  </td>
                  <td>
                    {cert.isVerified ? (
                      <span className="status-badge verified">
                        <i className="fas fa-check-circle"></i>
                        Verified
                      </span>
                    ) : (
                      <span className="status-badge pending">
                        <i className="fas fa-clock"></i>
                        Pending
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      className="action-btn view"
                      onClick={() => viewCertificate(cert.certificateId)}
                      title="View Details"
                    >
                      <i className="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <i className="fas fa-angle-left"></i>
            Previous
          </button>

          <div className="page-numbers">
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = currentPage - 2 + i;
              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                  >
                    {pageNum}
                  </button>
                );
              }
              return null;
            })}
          </div>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Next
            <i className="fas fa-angle-right"></i>
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      )}

      {/* View Modal */}
      {showModal && selectedCertificate && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-certificate"></i>
                Certificate Details
              </h2>
              <button onClick={() => setShowModal(false)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="cert-details-grid">
                <div className="detail-item">
                  <label>Certificate ID:</label>
                  <span>{selectedCertificate.certificateId}</span>
                </div>
                <div className="detail-item">
                  <label>Voter ID:</label>
                  <span>{selectedCertificate.voterId}</span>
                </div>
                <div className="detail-item">
                  <label>Full Name:</label>
                  <span>{selectedCertificate.fullName}</span>
                </div>
                <div className="detail-item">
                  <label>Phone Number:</label>
                  <span>{selectedCertificate.phoneNumber}</span>
                </div>
                <div className="detail-item">
                  <label>Age:</label>
                  <span>{selectedCertificate.age} years</span>
                </div>
                <div className="detail-item">
                  <label>Gender:</label>
                  <span>{selectedCertificate.gender}</span>
                </div>
                <div className="detail-item">
                  <label>District:</label>
                  <span>{selectedCertificate.district}</span>
                </div>
                <div className="detail-item">
                  <label>Constituency:</label>
                  <span>{selectedCertificate.constituency}</span>
                </div>
                <div className="detail-item">
                  <label>Submitted At:</label>
                  <span>{new Date(selectedCertificate.submittedAt).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Download Count:</label>
                  <span>{selectedCertificate.downloadCount}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowModal(false)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVoterCertificates;
